import React, { useState } from "react";
import {
  CheckCircle,
  ChevronLeft,
  ArrowLeft,
  Users,
  Upload,
  X,
  Plus,
  Link,
  FileText,
  Video,
  Share2,
} from "lucide-react";
import {
  trackApplicationEvent,
  trackHackathonEvent,
  trackButtonClick,
  HACKATHON_EVENTS,
} from "../../../Services/analytics";

const ApplicationForm = ({
  applicationData,
  applicationStep,
  isLoading,
  error,
  onDataChange,
  onStepChange,
  onSubmit,
  onGoBack,
  // New props for team integration
  currentTeam,
  teamMembers,
  isTeamLead,
  onGoToTeams,
  categories = [],
}) => {
  const [fieldErrors, setFieldErrors] = useState({});

  // Force team creation if no team exists
  if (!currentTeam) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-3 sm:p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-4 sm:p-8 text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-custom-blue-1/10 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <Users className="w-6 h-6 sm:w-8 sm:h-8 text-custom-blue-1" />
          </div>
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
            Team Required
          </h2>
          <p className="text-gray-600 mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base">
            You must create or join a team before you can submit an application
            for the hackathon. Teams are required for all participants.
          </p>
          <button
            onClick={onGoToTeams}
            className="w-full flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-custom-blue-1 text-white rounded-xl hover:bg-custom-blue-1/90 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] text-sm sm:text-base"
          >
            <Users className="w-4 h-4 sm:w-5 sm:h-5" />
            Create or Join Team
          </button>
        </div>
      </div>
    );
  }

  // Prevent non-team leads from submitting applications
  if (currentTeam && !isTeamLead) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-3 sm:p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-4 sm:p-8 text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <Users className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
          </div>
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
            Team Leader Only
          </h2>
          <p className="text-gray-600 mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base">
            Only team leaders can submit applications for the hackathon. Please
            contact your team leader to submit the application on behalf of your
            team.
          </p>
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs sm:text-sm text-blue-800 font-medium">
              Current Team:{" "}
              <span className="font-bold">{currentTeam.name}</span>
            </p>
            <p className="text-xs sm:text-sm text-blue-600 mt-1">
              You are a team member, not the team leader.
            </p>
          </div>
          <button
            onClick={onGoBack}
            className="w-full flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const handleInputChange = (field, value) => {
    onDataChange(field, value);

    // Track only crucial form interactions
    if (field === "category" && value) {
      trackHackathonEvent(HACKATHON_EVENTS.CATEGORY_SELECTED, {
        category: value,
        team_name: currentTeam?.name,
      });
    }

    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (PDF only)
      const allowedTypes = ["application/pdf"];

      if (!allowedTypes.includes(file.type)) {
        setFieldErrors((prev) => ({
          ...prev,
          pitchDeckFile: "Please upload a PDF file only",
        }));
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setFieldErrors((prev) => ({
          ...prev,
          pitchDeckFile: "File size must be less than 10MB",
        }));
        return;
      }

      // Track successful file upload
      trackHackathonEvent(HACKATHON_EVENTS.PITCH_DECK_UPLOADED, {
        file_name: file.name,
        file_size: file.size,
        team_name: currentTeam?.name,
      });

      handleInputChange("pitchDeckFile", file);
    }
  };

  const addSocialMediaPost = () => {
    const currentPosts = applicationData.socialMediaPosts || [""];
    handleInputChange("socialMediaPosts", [...currentPosts, ""]);
  };

  const removeSocialMediaPost = (index) => {
    const currentPosts = applicationData.socialMediaPosts || [""];
    if (currentPosts.length > 1) {
      const newPosts = currentPosts.filter((_, i) => i !== index);
      handleInputChange("socialMediaPosts", newPosts);
    }
  };

  const updateSocialMediaPost = (index, value) => {
    const currentPosts = [...(applicationData.socialMediaPosts || [""])];
    currentPosts[index] = value;
    handleInputChange("socialMediaPosts", currentPosts);
  };

  const validateField = (field, value) => {
    const requiredFields = [
      "teamName",
      "teamSize",
      "teamLeaderName",
      "teamLeaderEmail",
      "teamLeaderPhone",
      "category",
      "availability",
      "timeCommitment",
      "pitchDeckFile",
      "youtubePitchDeck",
      "projectDemoLink",
    ];

    if (requiredFields.includes(field)) {
      if (!value || value === "" || (typeof value === "boolean" && !value)) {
        return "This field is required";
      }
    }

    // Email validation
    if (field === "teamLeaderEmail" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return "Please enter a valid email address";
      }
    }

    // Phone validation
    if (field === "teamLeaderPhone" && value) {
      const phoneRegex = /^[+]?[0-9\s\-()]{10,}$/;
      if (!phoneRegex.test(value)) {
        return "Please enter a valid phone number";
      }
    }

    // Time commitment validation
    if (field === "timeCommitment" && value) {
      const hours = parseInt(value);
      if (isNaN(hours) || hours < 1 || hours > 168) {
        return "Please enter a valid number of hours (1-168)";
      }
    }

    // URL validation for YouTube and demo links
    if (
      (field === "youtubePitchDeck" || field === "projectDemoLink") &&
      value
    ) {
      try {
        new URL(value);
      } catch {
        return "Please enter a valid URL";
      }
    }

    // YouTube URL specific validation
    if (field === "youtubePitchDeck" && value) {
      const youtubeRegex =
        /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
      if (!youtubeRegex.test(value)) {
        return "Please enter a valid YouTube URL";
      }
    }

    return "";
  };

  const validateCurrentStep = () => {
    const stepFields = {
      1: currentTeam
        ? []
        : [
            "teamName",
            "teamSize",
            "teamLeaderName",
            "teamLeaderEmail",
            "teamLeaderPhone",
          ],
      2: ["category", "availability", "timeCommitment"],
      3: ["pitchDeckFile", "youtubePitchDeck", "projectDemoLink"],
      4: [],
    };

    const currentFields = stepFields[applicationStep] || [];
    const errors = {};
    let hasErrors = false;

    currentFields.forEach((field) => {
      const error = validateField(field, applicationData[field]);
      if (error) {
        errors[field] = error;
        hasErrors = true;
      }
    });

    // Check consent checkboxes on last step
    if (applicationStep === 4) {
      const requiredConsents = [
        "agreeToTerms",
        "consentToReview",
        "consentToPublicity",
        "commitToParticipate",
      ];
      requiredConsents.forEach((consent) => {
        if (!applicationData[consent]) {
          errors[consent] = "This consent is required";
          hasErrors = true;
        }
      });
    }

    setFieldErrors(errors);
    return !hasErrors;
  };
  const renderApplicationStepContent = () => {
    switch (applicationStep) {
      case 1:
        return (
          <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 gap-3 sm:gap-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                Team Information
              </h3>
              {currentTeam && isTeamLead && (
                <button
                  type="button"
                  onClick={onGoToTeams}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-custom-blue-1/80 transition-colors text-xs sm:text-sm"
                >
                  <span className="hidden sm:inline">Edit Team Settings</span>
                  <span className="sm:hidden">Edit Team</span>
                </button>
              )}
            </div>

            {currentTeam ? (
              <div className="space-y-4 sm:space-y-6">
                <div className="bg-custom-blue-1/10 border border-custom-blue-1/30 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-custom-blue-1" />
                    <span className="text-custom-blue-1 font-medium text-sm sm:text-base">
                      Team information loaded from your team
                    </span>
                  </div>
                  <p className="text-custom-blue-1/80 text-xs sm:text-sm">
                    {isTeamLead
                      ? "As team leader, you can edit team settings using the button above."
                      : "Team information is managed by your team leader."}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-2">
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700">
                      Team Name
                    </label>
                    <div className="w-full px-3 sm:px-4 py-3 sm:py-4 border border-gray-200 rounded-xl bg-gray-50 text-gray-600 text-sm sm:text-base">
                      {currentTeam.name || "Team Name"}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700">
                      Team Size
                    </label>
                    <div className="w-full px-3 sm:px-4 py-3 sm:py-4 border border-gray-200 rounded-xl bg-gray-50 text-gray-600 text-sm sm:text-base">
                      {currentTeam.members?.length || "1"} members
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Team Leader Name
                    </label>
                    <div className="w-full px-4 py-4 border border-gray-200 rounded-xl bg-gray-50 text-gray-600">
                      {teamMembers.find((member) => member.isOwner)
                        ?.displayName || "Team Leader"}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Team Leader Email
                    </label>
                    <div className="w-full px-4 py-4 border border-gray-200 rounded-xl bg-gray-50 text-gray-600">
                      {teamMembers.find((member) => member.isOwner)?.email ||
                        "team@example.com"}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Team Leader Phone
                    </label>
                    <div className="w-full px-4 py-4 border border-gray-200 rounded-xl bg-gray-50 text-gray-600">
                      {teamMembers.find((member) => member.isOwner)
                        ?.phoneNumber || "Phone Number"}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // No team - manual input (this shouldn't happen due to team requirement check above)
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Team Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={applicationData.teamName}
                    onChange={(e) =>
                      handleInputChange("teamName", e.target.value)
                    }
                    className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:ring-custom-blue-1/20 focus:border-custom-blue-1 transition-all duration-300 bg-white/80 ${
                      fieldErrors.teamName
                        ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                        : "border-gray-200"
                    }`}
                    placeholder="Enter your team name"
                    required
                  />
                  {fieldErrors.teamName && (
                    <p className="text-sm text-red-600 mt-1">
                      {fieldErrors.teamName}
                    </p>
                  )}
                </div>
                {/* Add other manual input fields here if needed */}
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Category & Availability
            </h3>

            <div className="grid grid-cols-1 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={applicationData.category}
                  onChange={(e) => {
                    handleInputChange("category", e.target.value);
                    // Track category selection
                    if (e.target.value) {
                      trackHackathonEvent(HACKATHON_EVENTS.CATEGORY_SELECTED, {
                        category: e.target.value,
                        team_name: currentTeam?.name,
                      });
                    }
                  }}
                  className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:ring-custom-blue-1/20 focus:border-custom-blue-1 transition-all duration-300 bg-white/80 ${
                    fieldErrors.category
                      ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                      : "border-gray-200"
                  }`}
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.categoryName}>
                      {category.categoryName}
                    </option>
                  ))}
                </select>
                {fieldErrors.category && (
                  <p className="text-sm text-red-600 mt-1">
                    {fieldErrors.category}
                  </p>
                )}
                {applicationData.category &&
                  categories.find(
                    (cat) => cat.categoryName === applicationData.category
                  ) && (
                    <p className="text-sm text-gray-600 mt-1">
                      {
                        categories.find(
                          (cat) => cat.categoryName === applicationData.category
                        ).categoryDescription
                      }
                    </p>
                  )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Availability <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={applicationData.availability}
                    onChange={(e) =>
                      handleInputChange("availability", e.target.value)
                    }
                    className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:ring-custom-blue-1/20 focus:border-custom-blue-1 transition-all duration-300 bg-white/80 ${
                      fieldErrors.availability
                        ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                        : "border-gray-200"
                    }`}
                    required
                  >
                    <option value="">Select your availability</option>
                    <option value="full-time">
                      Full-time (entire duration)
                    </option>
                    <option value="part-time">Part-time</option>
                    <option value="weekends">Weekends only</option>
                    <option value="flexible">Flexible schedule</option>
                  </select>
                  {fieldErrors.availability && (
                    <p className="text-sm text-red-600 mt-1">
                      {fieldErrors.availability}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Time Commitment (hours per week){" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={applicationData.timeCommitment}
                    onChange={(e) =>
                      handleInputChange("timeCommitment", e.target.value)
                    }
                    className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:ring-custom-blue-1/20 focus:border-custom-blue-1 transition-all duration-300 bg-white/80 ${
                      fieldErrors.timeCommitment
                        ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                        : "border-gray-200"
                    }`}
                    placeholder="e.g., 20"
                    min="1"
                    max="168"
                    required
                  />
                  {fieldErrors.timeCommitment && (
                    <p className="text-sm text-red-600 mt-1">
                      {fieldErrors.timeCommitment}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Submission Requirements
            </h3>

            {/* Pitch Deck Upload */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-custom-blue-1" />
                <h4 className="text-md font-semibold text-gray-800">
                  Pitch Deck Upload (PDF){" "}
                  <span className="text-red-500">*</span>
                </h4>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Outline problem, solution, technology, team, market fit, and
                impact.
              </p>
              <div className="space-y-2">
                <label className="block">
                  <div
                    className={`w-full px-4 py-6 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300 ${
                      fieldErrors.pitchDeckFile
                        ? "border-red-300 bg-red-50"
                        : applicationData.pitchDeckFile
                        ? "border-green-400 bg-green-50 hover:border-green-500"
                        : "border-gray-300 hover:border-custom-blue-1 hover:bg-custom-blue-1/5"
                    }`}
                  >
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <div className="text-center">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      {applicationData.pitchDeckFile ? (
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {applicationData.pitchDeckFile.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            Click to change file
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            Click to upload pitch deck
                          </p>
                          <p className="text-xs text-gray-500">
                            PDF files only (max 10MB)
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </label>
                {fieldErrors.pitchDeckFile && (
                  <p className="text-sm text-red-600 mt-1">
                    {fieldErrors.pitchDeckFile}
                  </p>
                )}
              </div>
            </div>

            {/* Video Pitch Link */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Video className="w-5 h-5 text-custom-blue-1" />
                <h4 className="text-md font-semibold text-gray-800">
                  Video Pitch Link (YouTube){" "}
                  <span className="text-red-500">*</span>
                </h4>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                A 3–5 minute video explaining your project and demonstrating any
                prototype.
              </p>
              <div className="space-y-2">
                <input
                  type="url"
                  value={applicationData.youtubePitchDeck}
                  onChange={(e) => {
                    handleInputChange("youtubePitchDeck", e.target.value);
                    // Track when a valid YouTube URL is entered
                    if (
                      e.target.value &&
                      /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/.test(
                        e.target.value
                      )
                    ) {
                      trackHackathonEvent(
                        HACKATHON_EVENTS.VIDEO_PITCH_SUBMITTED,
                        {
                          video_url: e.target.value,
                          team_name: currentTeam?.name,
                        }
                      );
                    }
                  }}
                  className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:ring-custom-blue-1/20 focus:border-custom-blue-1 transition-all duration-300 bg-white/80 ${
                    fieldErrors.youtubePitchDeck
                      ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                      : "border-gray-200"
                  }`}
                  placeholder="https://www.youtube.com/watch?v=..."
                  required
                />
                {fieldErrors.youtubePitchDeck && (
                  <p className="text-sm text-red-600 mt-1">
                    {fieldErrors.youtubePitchDeck}
                  </p>
                )}
              </div>
            </div>

            {/* Project Demo Link */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Link className="w-5 h-5 text-custom-blue-1" />
                <h4 className="text-md font-semibold text-gray-800">
                  Project Demo / Hosted Link{" "}
                  <span className="text-red-500">*</span>
                </h4>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Provide the URL of your project or prototype hosted on the ABQ Portal or any cloud service.
              </p>
              <div className="space-y-2">
                <input
                  type="url"
                  value={applicationData.projectDemoLink}
                  onChange={(e) =>
                    handleInputChange("projectDemoLink", e.target.value)
                  }
                  className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:ring-custom-blue-1/20 focus:border-custom-blue-1 transition-all duration-300 bg-white/80 ${
                    fieldErrors.projectDemoLink
                      ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                      : "border-gray-200"
                  }`}
                  placeholder="https://your-project-demo.com"
                  required
                />
                {fieldErrors.projectDemoLink && (
                  <p className="text-sm text-red-600 mt-1">
                    {fieldErrors.projectDemoLink}
                  </p>
                )}
              </div>
            </div>

            {/* Social Media Confirmation */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Share2 className="w-5 h-5 text-custom-blue-1" />
                <h4 className="text-md font-semibold text-gray-800">
                  Social Media Confirmation
                </h4>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Share your project on your team's social media and tag STI-OP /
                Industry 4.0+. Provide links/screenshots of the posts.
              </p>
              <div className="space-y-3">
                {(applicationData.socialMediaPosts || [""]).map(
                  (post, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="url"
                        value={post}
                        onChange={(e) =>
                          updateSocialMediaPost(index, e.target.value)
                        }
                        className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-custom-blue-1/20 focus:border-custom-blue-1 transition-all duration-300 bg-white/80"
                        placeholder="https://social-media-post-link.com"
                      />
                      {(applicationData.socialMediaPosts || [""]).length >
                        1 && (
                        <button
                          type="button"
                          onClick={() => removeSocialMediaPost(index)}
                          className="px-3 py-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )
                )}
                <button
                  type="button"
                  onClick={addSocialMediaPost}
                  className="flex items-center gap-2 px-4 py-2 text-custom-blue-1 hover:bg-custom-blue-1/5 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Another Post
                </button>
              </div>
            </div>

            {/* Referral Source */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                How did you hear about this hackathon?
              </label>
              <select
                value={applicationData.referralSource}
                onChange={(e) =>
                  handleInputChange("referralSource", e.target.value)
                }
                className="w-full px-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-custom-blue-1/20 focus:border-custom-blue-1 transition-all duration-300 bg-white/80"
              >
                <option value="">Select an option</option>
                <option value="social-media">Social Media</option>
                <option value="website">Website</option>
                <option value="friend">Friend/Colleague</option>
                <option value="university">University/School</option>
                <option value="email">Email Newsletter</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Declarations & Consent
            </h3>

            <div className="space-y-6">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  checked={applicationData.agreeToTerms}
                  onChange={(e) =>
                    handleInputChange("agreeToTerms", e.target.checked)
                  }
                  className={`mt-1 w-4 h-4 text-custom-blue-1 border-gray-300 rounded focus:ring-custom-blue-1 ${
                    fieldErrors.agreeToTerms ? "border-red-300" : ""
                  }`}
                  required
                />
                <div className="flex-1">
                  <label
                    htmlFor="agreeToTerms"
                    className="text-sm text-gray-700"
                  >
                    <span className="text-red-500">*</span> We confirm that the
                    information submitted is accurate and that all team members
                    agree to participate in the Industry 4.0+ Hackathon.
                  </label>
                  {fieldErrors.agreeToTerms && (
                    <p className="text-sm text-red-600 mt-1">
                      {fieldErrors.agreeToTerms}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="consentToReview"
                  checked={applicationData.consentToReview}
                  onChange={(e) =>
                    handleInputChange("consentToReview", e.target.checked)
                  }
                  className={`mt-1 w-4 h-4 text-custom-blue-1 border-gray-300 rounded focus:ring-custom-blue-1 ${
                    fieldErrors.consentToReview ? "border-red-300" : ""
                  }`}
                  required
                />
                <div className="flex-1">
                  <label
                    htmlFor="consentToReview"
                    className="text-sm text-gray-700"
                  >
                    <span className="text-red-500">*</span> We understand that
                    our submission will be reviewed by mentors, judges, and
                    organizers for the purposes of shortlisting and evaluation.
                  </label>
                  {fieldErrors.consentToReview && (
                    <p className="text-sm text-red-600 mt-1">
                      {fieldErrors.consentToReview}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="consentToPublicity"
                  checked={applicationData.consentToPublicity}
                  onChange={(e) =>
                    handleInputChange("consentToPublicity", e.target.checked)
                  }
                  className={`mt-1 w-4 h-4 text-custom-blue-1 border-gray-300 rounded focus:ring-custom-blue-1 ${
                    fieldErrors.consentToPublicity ? "border-red-300" : ""
                  }`}
                  required
                />
                <div className="flex-1">
                  <label
                    htmlFor="consentToPublicity"
                    className="text-sm text-gray-700"
                  >
                    <span className="text-red-500">*</span> We consent to have
                    our team/project featured in hackathon publicity (media,
                    website, and official reports).
                  </label>
                  {fieldErrors.consentToPublicity && (
                    <p className="text-sm text-red-600 mt-1">
                      {fieldErrors.consentToPublicity}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="commitToParticipate"
                  checked={applicationData.commitToParticipate}
                  onChange={(e) =>
                    handleInputChange("commitToParticipate", e.target.checked)
                  }
                  className={`mt-1 w-4 h-4 text-custom-blue-1 border-gray-300 rounded focus:ring-custom-blue-1 ${
                    fieldErrors.commitToParticipate ? "border-red-300" : ""
                  }`}
                  required
                />
                <div className="flex-1">
                  <label
                    htmlFor="commitToParticipate"
                    className="text-sm text-gray-700"
                  >
                    <span className="text-red-500">*</span> We commit to
                    participate fully in all phases of the hackathon if selected
                    (mentorship, hacking session, pitching).
                  </label>
                  {fieldErrors.commitToParticipate && (
                    <p className="text-sm text-red-600 mt-1">
                      {fieldErrors.commitToParticipate}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Team Leader Digital Acknowledgement
                </h4>
                <p className="text-sm text-gray-600">
                  By submitting this application, the team leader digitally
                  acknowledges and signs this form on behalf of the team on{" "}
                  {new Date().toLocaleDateString()}.
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // This progress calculation is now handled in the dashboard

  const handleApplicationSubmit = (e) => {
    e.preventDefault();
    if (validateCurrentStep()) {
      // Track application submission
      trackApplicationEvent(
        HACKATHON_EVENTS.APPLICATION_SUBMITTED,
        applicationStep,
        {
          team_name: currentTeam?.name,
          category: applicationData.category,
          team_size: currentTeam?.members?.length,
        }
      );

      trackHackathonEvent(HACKATHON_EVENTS.APPLICATION_SUBMITTED, {
        team_name: currentTeam?.name,
        category: applicationData.category,
        step_completed: applicationStep,
      });

      onSubmit();
    }
  };

  return (
    <div className="px-3 sm:px-0">
      {/* Back Button */}
      <div className="mb-4 sm:mb-6">
        <button
          onClick={onGoBack}
          className="flex items-center gap-2 text-custom-blue-1 hover:text-custom-blue-1/80 transition-colors text-sm sm:text-base"
        >
          <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
          Back to Dashboard
        </button>
      </div>

      <div className="text-center items-start mb-6 sm:mb-10">
        <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
          Hackathon Application Form
        </h2>
        <p className="text-gray-600 text-sm sm:text-lg">
          Complete all sections to submit your application
        </p>
      </div>

      <form
        onSubmit={handleApplicationSubmit}
        className="space-y-4 sm:space-y-8"
      >
        <div className="bg-white/50 rounded-2xl p-4 sm:p-8 border border-gray-100">
          {renderApplicationStepContent()}
        </div>

        {error && (
          <div className="p-3 sm:p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 sm:gap-3 animate-in slide-in-from-top-2">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-500 text-sm sm:text-lg">!</span>
            </div>
            <span className="text-xs sm:text-sm text-red-700 font-medium">
              {error}
            </span>
          </div>
        )}

        {/* Enhanced Navigation Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-center pt-4 sm:pt-8 border-t border-gray-100 gap-3 sm:gap-0">
          <button
            type="button"
            onClick={() => {
              onStepChange(Math.max(1, applicationStep - 1));
              // Smooth scroll to top of form section
              setTimeout(() => {
                window.scrollTo({ top: 300, behavior: "smooth" });
              }, 100);
            }}
            disabled={applicationStep === 1}
            className="w-full sm:w-auto group flex items-center justify-center gap-2 px-4 sm:px-8 py-3 sm:py-4 border-2 border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent text-sm sm:text-base"
          >
            <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4 transition-transform group-hover:-translate-x-1" />
            Previous
          </button>

          {applicationStep < 4 ? (
            <button
              type="button"
              onClick={() => {
                if (validateCurrentStep()) {
                  // Track step completion
                  trackApplicationEvent(
                    HACKATHON_EVENTS.APPLICATION_STEP_COMPLETED,
                    applicationStep,
                    {
                      next_step: applicationStep + 1,
                      team_name: currentTeam?.name,
                    }
                  );

                  onStepChange(applicationStep + 1);
                  // Smooth scroll to top of form section
                  setTimeout(() => {
                    window.scrollTo({ top: 300, behavior: "smooth" });
                  }, 100);
                }
              }}
              className="w-full sm:w-auto group flex items-center justify-center gap-2 px-4 sm:px-8 py-3 sm:py-4 bg-custom-blue-1 text-white rounded-xl hover:bg-custom-blue-1/90 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] text-sm sm:text-base"
            >
              Next
              <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4 rotate-180 transition-transform group-hover:translate-x-1" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={
                isLoading ||
                !(
                  applicationData.agreeToTerms &&
                  applicationData.consentToReview &&
                  applicationData.consentToPublicity &&
                  applicationData.commitToParticipate
                )
              }
              className="w-full sm:w-auto group flex items-center justify-center gap-2 px-4 sm:px-8 py-3 sm:py-4 bg-custom-green-1 text-white rounded-xl hover:bg-custom-green-1/90 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm sm:text-base"
            >
              {isLoading ? (
                <>
                  <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span className="hidden sm:inline">Submitting...</span>
                  <span className="sm:hidden">Submitting...</span>
                </>
              ) : (
                <>
                  <span className="hidden sm:inline">Submit Application</span>
                  <span className="sm:hidden">Submit</span>
                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                </>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ApplicationForm;
